import { makeStyles } from '@material-ui/styles'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  getPortsForVm,
  listNetworks,
  updatePort,
} from 'app/plugins/openstack/components/networks/actions'
import { Port } from 'app/plugins/openstack/components/networks/network-details/model'
import { listSecurityGroups } from 'app/plugins/openstack/components/networks/security-groups/actions'
import {
  securityGroupsByIdSelector,
  securityGroupsSelector,
} from 'app/plugins/openstack/components/networks/security-groups/selectors'
import { networksByIdSelector } from 'app/plugins/openstack/components/networks/selectors'
import { securityGroupColumns } from 'app/plugins/openstack/components/vms/helpers'
import VmPortsPicklist from 'app/plugins/openstack/components/vms/VmPortsPicklist'
import Alert from 'core/components/Alert'
import { getFieldsForCard } from 'core/components/InfoPanel'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import PicklistField from 'core/components/validatedForm/DropdownField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { PropertiesTable } from 'k8s/components/kaapi/cluster-upgrade/PropertyCardTable'

const defaultParams = {
  enablePortSecurity: false,
  securityGroups: [],
  port: null,
}

const metadataFields = (networksById) => [
  { id: 'id', title: 'Port', render: (port, item) => item?.name || item?.id },
  {
    id: 'network_id',
    title: 'Network',
    render: (port, item) => networksById?.[item?.network_id]?.name || item?.network_id,
  },
  {
    id: 'mac_address',
    title: 'MAC Address',
  },
  {
    id: 'fixed_ips',
    title: 'Private IP(s)',
    render: (port, item) => item?.fixed_ips?.map((ip) => ip?.ip_address)?.join(', '),
  },
]

const EditVmPortSecurityModal = ({ rows: vms, onClose }) => {
  const vm = vms?.[0]
  const classes = useStyles()
  const { params, getParamsUpdater, updateParams } = useParams(defaultParams)

  const [vmPorts, setVmPorts] = useState<Port[]>([])
  const [loadingVmPorts, setLoadingVmPorts] = useState<boolean>(true)
  const [errorVmPorts, setErrorVmPorts] = useState<string>('')

  const { loading: loadingSecurityGroups, reload: reloadSecurityGroups } =
    useListAction(listSecurityGroups)
  const { loading: loadingNetworks } = useListAction(listNetworks)

  const { update, updating, error, reset } = useUpdateAction(updatePort)

  const securityGroupsList = useSelector(securityGroupsSelector)
  const securityGroupsById = useSelector(securityGroupsByIdSelector)
  const networksById = useSelector(networksByIdSelector)

  const metadata = useMemo(() => {
    if (!params?.port) return []

    const fields = metadataFields(networksById)
    return getFieldsForCard(fields, params?.port)
  }, [params?.port, networksById])

  const handleOnSubmit = useCallback(async () => {
    const body = {
      port: {
        port_security_enabled: params?.enablePortSecurity,
        security_groups: params?.enablePortSecurity
          ? params?.securityGroups?.map((securityGroup) => securityGroup?.id)
          : [],
      },
    }
    const { success } = await update({ id: params?.port?.id, body })
    if (success) {
      onClose()
      reset()
    }
  }, [params])

  const handlePortChange = useCallback(
    (portId) => {
      // If selected port is cleared, reset the form
      if (!portId) {
        updateParams(defaultParams)
        setErrorVmPorts('')
        return
      }

      const selectedPort = vmPorts?.find((port) => port?.id === portId)

      // Safety check: port list may change after modal loads (deleted/updated)
      if (!selectedPort) {
        setErrorVmPorts('The selected port is no longer available. Please select another port.')
        return
      }

      // get security groups for the selected port to pre-fill the ListTableField
      const securityGroups =
        selectedPort?.security_groups?.map((sgId) => securityGroupsById?.[sgId]) || []

      const enablePortSecurity = selectedPort?.port_security_enabled

      updateParams({
        port: selectedPort,
        securityGroups,
        enablePortSecurity,
      })
    },
    [vmPorts, securityGroupsById],
  )

  useEffect(() => {
    if (!vm?.id) {
      return
    }
    const getPorts = async () => {
      try {
        const response = await getPortsForVm({ vmId: vm?.id })
        setVmPorts(response)
      } catch (e) {
        setErrorVmPorts(e?.message || 'Error fetching VM ports')
      } finally {
        setLoadingVmPorts(false)
      }
    }
    getPorts()
  }, [vm?.id])

  return (
    <ModalForm
      open
      title={'Edit Port Security'}
      onSubmit={handleOnSubmit}
      onClose={onClose}
      submitting={updating}
      submitTitle={`Update Port Security`}
      loading={loadingNetworks}
      maxWidth={660}
      data-testid="edit-port-security-modal"
    >
      <div className={classes.container}>
        {(error || errorVmPorts) && (
          <Alert
            variant="error"
            message={error?.message || errorVmPorts}
            data-testid="edit-port-security-modal-error"
          />
        )}
        <Text variant="body2">Please select a VM port to apply port security</Text>
        <PicklistField
          DropdownComponent={VmPortsPicklist}
          id="port"
          label="VM Port"
          onChange={handlePortChange}
          value={params?.port?.id}
          vmPorts={vmPorts}
          loading={loadingVmPorts}
          required
          data-testid="edit-port-security-modal-picklist"
        />
        {params?.port?.id && <PropertiesTable fields={metadata} />}
        <div className={classes.checkbox}>
          <CheckboxField
            id="enablePortSecurity"
            label="Enable Port Security Groups"
            value={params?.enablePortSecurity}
            onChange={getParamsUpdater('enablePortSecurity')}
            info={
              !params?.port
                ? 'Please select a port first.'
                : 'If checked, enable security groups for selected port.'
            }
            disabled={!params?.port}
            data-testid="edit-port-security-modal-enable-port-security-checkbox"
          />
        </div>
        {params?.enablePortSecurity && (
          <ListTableField
            id="securityGroups"
            data={securityGroupsList}
            loading={loadingSecurityGroups}
            columns={securityGroupColumns}
            onChange={getParamsUpdater('securityGroups')}
            value={params?.securityGroups}
            uniqueIdentifier="id"
            searchTargets={['name']}
            multiSelection
            onReload={reloadSecurityGroups}
            required
            data-testid="edit-port-security-modal-security-groups-table"
          />
        )}
      </div>
    </ModalForm>
  )
}

export default EditVmPortSecurityModal

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'grid',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(8),
  },
  checkbox: { width: 'fit-content' },
}))
